import React from 'react';
import ReactDOM from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { GlobalContextProvider } from './context/GlobalContextProvider';
import { VERSION } from './components/Util';
import { initSession, serverRequest, getOS, setSession, setRunners, setSplashScreen } from './components/Util';
import { StatusBar, Style } from '@capacitor/status-bar';

// ---- 1) System UI (status bar) — runs once at startup
async function setupSystemUI() {
  try {
    const platform = Capacitor.getPlatform();
      if (platform === 'ios' || platform === 'android') {
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setStyle({ style: Style.Dark });
      }
  } catch (e) {
    console.warn('StatusBar setup skipped:', e);
  }
}

// ---- 2) MUI theme (unchanged)
const theme = createTheme({
  typography: {
    fontFamily: ['"MaisonNeue"', '"Segoe UI"', '"Helvetica Neue"'].join(','),
  },
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#ED1C24' },
  },
});

// ---- 3) Create root and RENDER IMMEDIATELY
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

function renderApp() {
  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <GlobalContextProvider>
          <App />
        </GlobalContextProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

// Optional helper so TypeScript lets us log errors safely
function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try { return JSON.stringify(e); } catch { return String(e); }
}

async function bootStrict() {
  console.log('[boot] start');

  // 1) system UI (non-blocking, but keep it first)
  await setupSystemUI();

  // 2) session init + LOGIN (must be first network)
  console.log('[boot] login…');
  await initSession();
  const loginRes = await serverRequest(`login?os=${getOS()}&version=${VERSION}`);
  setSession(loginRes.session);
  setSplashScreen(loginRes.runners, loginRes.finishers, loginRes.meetings);

  // 3) RUNNERS (must come right after login)
  console.log('[boot] runners…');
  const runners = await serverRequest('runners');
  setRunners(runners);

  console.log('[boot] core ready');
}

(async () => {
  try {
    // 🔒 Do NOT render until core boot finishes
    await bootStrict();
  } catch (e) {
    console.error('[boot] failed:', errorMessage(e));
    // You can render an error page or keep the splash; still render the app so UI is visible:
    // (If you want to stay on splash until retry, comment out renderApp())
  }
  renderApp();
})();

reportWebVitals();
